import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TuiAlertService, TuiButton, TuiLoader, TuiTextfield } from '@taiga-ui/core';
import { TuiInputModule, TuiInputDateRangeModule, TuiTextfieldControllerModule } from '@taiga-ui/legacy';
import { TuiTable } from '@taiga-ui/addon-table';
import { TuiDay, TuiDayRange } from '@taiga-ui/cdk';
import { TuiSearch } from '@taiga-ui/layout';

import { ReportsService, ReportData } from '../../../core/services/reports.service';
import { ClientArchivedBadgeComponent } from '../../../shared/components/client-archived-badge/client-archived-badge.component';
import { reportArchivedStatus } from '../report-export.util';
import printJS from 'print-js';
import * as XLSX from 'xlsx';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';

@Component({
  selector: 'app-registered-clients-report',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TuiTextfield,
    TuiButton,
    TuiLoader,
    TuiInputModule,
    TuiInputDateRangeModule,
    TuiTextfieldControllerModule,
    TuiTable,
    TuiSearch,
    ClientArchivedBadgeComponent,
  ],
  templateUrl: './registered-clients-report.component.html',
  styleUrl: './registered-clients-report.component.scss',
  providers: [DatePipe],
})
export class RegisteredClientsReportComponent implements OnInit {
  private reportsService = inject(ReportsService);
  private alerts = inject(TuiAlertService);
  private datePipe = inject(DatePipe);

  reportData: ReportData[] = [];
  filteredData: ReportData[] = [];
  loading = false;

  // Filter Type
  rangeFilterTypes: string[] = ['Monthly', 'Custom Range'];
  rangeFilterControl = new FormControl<string>('Monthly');

  // Filters
  searchControl = new FormControl('');
  monthControl = new FormControl<number>(new Date().getMonth());
  yearControl = new FormControl<number>(new Date().getFullYear());
  customRangeControl = new FormControl<TuiDayRange | null>(null);
  selectedMonth: Date | null = null;
  selectedDateRange: { startDate: Date; endDate: Date } | null = null;

  // Month options
  months = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' },
  ];

  // Year options (last 5 years to next 2 years)
  years: number[] = [];

  generateYears(): void {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 2; i++) {
      this.years.push(i);
    }
  }

  // Pagination
  currentPage = 1;
  pageSize = 100;
  totalRecords = 0;
  totalPages = 0;

  // Totals
  totalAdditionalCharges = 0;
  totalShiftCharges = 0;
  grandTotal = 0;

  // Active report subscription (used to cancel previous requests)
  private reportSub: Subscription | null = null;

  ngOnInit(): void {
    // Generate years array
    this.generateYears();

    // Set default to current month
    const today = new Date();
    this.selectedMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.monthControl.setValue(today.getMonth());
    this.yearControl.setValue(today.getFullYear());

    this.setupRangeFilterTypeListener();
    this.loadReportData();
  }

  ngOnDestroy(): void {
    if (this.reportSub) {
      this.reportSub.unsubscribe();
      this.reportSub = null;
    }
  }

  applyFilters(): void {
    // Update selected values based on filter type
    const filterType = this.rangeFilterControl.value;
    
    if (filterType === 'Monthly') {
      const month = this.monthControl.value;
      const year = this.yearControl.value;
      if (month !== null && year !== null) {
        this.selectedMonth = new Date(year, month, 1);
        this.selectedDateRange = null;
      }
    } else if (filterType === 'Custom Range' && this.customRangeControl.value) {
      const value = this.customRangeControl.value;
      const from = value.from;
      const to = value.to;
      if (from && to) {
        this.selectedDateRange = {
          startDate: new Date(from.year, from.month, from.day),
          endDate: new Date(to.year, to.month, to.day),
        };
        this.selectedMonth = null;
      }
    }
    
    this.currentPage = 1;
    this.loadReportData();
  }

  setupRangeFilterTypeListener(): void {
    this.rangeFilterControl.valueChanges.subscribe((value) => {
      if (value === 'Monthly') {
        // Reset to current month when switching to Monthly
        const today = new Date();
        this.monthControl.setValue(today.getMonth());
        this.yearControl.setValue(today.getFullYear());
        this.customRangeControl.setValue(null);
      } else if (value === 'Custom Range') {
        this.customRangeControl.setValue(null);
      }
    });
  }

  loadReportData(): void {
    this.loading = true;

    let filters: any = {
      searchText: this.searchControl.value || '',
    };

    // Add date filters based on range type
    if (this.rangeFilterControl.value === 'Monthly' && this.selectedMonth) {
      filters.month = this.selectedMonth.getMonth() + 1;
      filters.year = this.selectedMonth.getFullYear();
    } else if (this.rangeFilterControl.value === 'Custom Range' && this.selectedDateRange) {
      filters.startDate = this.selectedDateRange.startDate.toISOString();
      filters.endDate = this.selectedDateRange.endDate.toISOString();
    }

    const payload = {
      pageIndex: this.currentPage,
      pageSize: this.pageSize,
      filters,
    };

    // Cancel previous in-flight report request if any
    if (this.reportSub) {
      try {
        this.reportSub.unsubscribe();
      } catch (e) {
        // ignore errors from unsubscribe
      }
      this.reportSub = null;
    }

    this.reportSub = this.reportsService.getRegisteredClientsReport(payload).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // Convert date strings to Date objects
          this.reportData = response.data.reportData.map((item) => ({
            ...item,
            invoiceDate: new Date(item.invoiceDate),
          }));
          this.filteredData = this.reportData;

          // Set totals from backend
          this.totalShiftCharges = response.data.totals.totalShiftCharges;
          this.totalAdditionalCharges = response.data.totals.totalAdditionalCharges;
          this.grandTotal = response.data.totals.grandTotal;

          // Set pagination
          this.totalRecords = response.data.pagination.total;
          this.totalPages = response.data.pagination.totalPages;
        }
        this.loading = false;
        // clear completed subscription reference
        if (this.reportSub) {
          this.reportSub.unsubscribe();
          this.reportSub = null;
        }
      },
      error: (error) => {
        console.error('Error loading report:', error);
        this.loading = false;
        this.alerts
          .open('Error loading report data', { appearance: 'error' })
          .subscribe();
        if (this.reportSub) {
          this.reportSub.unsubscribe();
          this.reportSub = null;
        }
      },
    });
  }

  clearFilters(): void {
    this.searchControl.setValue('');
    this.rangeFilterControl.setValue('Monthly');
    
    // Set default to current month
    const today = new Date();
    this.selectedMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.monthControl.setValue(today.getMonth());
    this.yearControl.setValue(today.getFullYear());
    this.customRangeControl.setValue(null);
    this.selectedDateRange = null;
    this.currentPage = 1;
    this.loadReportData();
  }

  printReport(): void {
    setTimeout(() => {
      printJS({
        printable: 'printable-report',
        type: 'html',
        css: ['styles.css'],
        targetStyles: ['*'],
        documentTitle: 'Registered Clients Report',
        header: this.getPrintHeader(),
      });
    }, 300);
  }

  exportToExcel(): void {
    const data = this.filteredData.map((item) => ({
      'Invoice Date': this.datePipe.transform(item.invoiceDate, 'MM/dd/yyyy'),
      'Invoice Number': item.invoiceNumber,
      'Client Ref No': item.clientRefNo,
      Status: reportArchivedStatus(item.clientArchived),
      'Client Name': item.clientName,
      'Shift Charges': item.shiftCharges.toFixed(2),
      'Additional Charges': item.additionalCharges.toFixed(2),
      Total: item.total.toFixed(2),
    }));

    // Add totals row
    data.push({
      'Invoice Date': '',
      'Invoice Number': '',
      'Client Ref No': '',
      Status: '',
      'Client Name': 'TOTALS',
      'Shift Charges': this.totalShiftCharges.toFixed(2),
      'Additional Charges': this.totalAdditionalCharges.toFixed(2),
      Total: this.grandTotal.toFixed(2),
    });

    // Create an empty worksheet, then add JSON starting at row 6 so header rows (1-5) remain for report title
    const worksheet = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_json(worksheet, data, { origin: 'A6' });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Registered Clients');

    // Add report header
    const reportName = 'Registered Clients Report';
    let dateRange = 'All Periods';
    if (this.rangeFilterControl.value === 'Monthly' && this.selectedMonth) {
      dateRange = this.datePipe.transform(this.selectedMonth, 'MMMM yyyy') || 'All Periods';
    } else if (this.rangeFilterControl.value === 'Custom Range' && this.selectedDateRange) {
      const start = this.datePipe.transform(this.selectedDateRange.startDate, 'MM/dd/yyyy');
      const end = this.datePipe.transform(this.selectedDateRange.endDate, 'MM/dd/yyyy');
      dateRange = `${start} - ${end}`;
    }
    const generated = this.datePipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');

    // Prepare header rows (will occupy rows 1-4, leave row 5 blank)
    const headerRows = [
      ['ROYAL HOSPITAL - HOME NURSING CARE'],
      [reportName],
      [`Date Range: ${dateRange}`],
      [`Generated: ${generated}`],
      [],
    ];

    XLSX.utils.sheet_add_aoa(worksheet, headerRows, { origin: 'A1' });

    // Determine number of columns to merge and center header across
    let colsCount = 8; // default columns
    if (data && data.length > 0) {
      colsCount = Object.keys(data[0]).length;
    }
    const lastColIndex = colsCount - 1;

    // Create merges for first 3 header lines (merge across all data columns)
    worksheet['!merges'] = worksheet['!merges'] || [];
    worksheet['!merges'].push({ s: { c: 0, r: 0 }, e: { c: lastColIndex, r: 0 } });
    worksheet['!merges'].push({ s: { c: 0, r: 1 }, e: { c: lastColIndex, r: 1 } });
    worksheet['!merges'].push({ s: { c: 0, r: 2 }, e: { c: lastColIndex, r: 2 } });

    // Apply styles to header cells (font sizes & bold)
    const setCellStyle = (cellRef: string, size: number) => {
      if (!worksheet[cellRef]) worksheet[cellRef] = { t: 's', v: '' } as any;
      worksheet[cellRef].s = {
        font: { bold: true, sz: size },
        alignment: { horizontal: 'center', vertical: 'center' },
      } as any;
    };

    // Style merged header cells by addressing the first column cells
    setCellStyle('A1', 16);
    setCellStyle('A2', 14);
    setCellStyle('A3', 12);
    setCellStyle('A4', 12);

    // Optionally set column widths for readability
    worksheet['!cols'] = [
      { wch: 15 }, // Invoice Date
      { wch: 18 }, // Invoice Number
      { wch: 18 }, // Client Ref No
      { wch: 12 }, // Status
      { wch: 30 }, // Client Name
      { wch: 14 }, // Shift Charges
      { wch: 18 }, // Additional Charges
      { wch: 14 }, // Total
    ];

    // Write file (try to enable cellStyles; fallback if unsupported)
    try {
      XLSX.writeFile(workbook, `Registered_Clients_Report_${Date.now()}.xlsx`, { bookType: 'xlsx', cellStyles: true });
    } catch (e) {
      XLSX.writeFile(workbook, `Registered_Clients_Report_${Date.now()}.xlsx`);
    }
  }

  getPrintHeader(): string {
    let dateRange = 'All Periods';
    if (this.rangeFilterControl.value === 'Monthly' && this.selectedMonth) {
      dateRange = this.datePipe.transform(this.selectedMonth, 'MMMM yyyy') || 'All Periods';
    } else if (this.rangeFilterControl.value === 'Custom Range' && this.selectedDateRange) {
      const start = this.datePipe.transform(this.selectedDateRange.startDate, 'MM/dd/yyyy');
      const end = this.datePipe.transform(this.selectedDateRange.endDate, 'MM/dd/yyyy');
      dateRange = `${start} - ${end}`;
    }
    const generated = this.datePipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');

    return `
      <div style="text-align: center; margin-bottom: 20px;">
        <h2 style="margin: 0; color: #00AEEF;">ROYAL HOSPITAL</h2>
        <h3 style="margin: 5px 0; color: #EC008C;">HOME NURSING CARE</h3>
        <h4 style="margin: 10px 0;">Registered Clients Report</h4>
        <p style="margin: 5px 0;">Date Range: ${dateRange}</p>
        <p style="margin: 5px 0; font-size: 12px;">Generated: ${generated}</p>
      </div>
    `;
  }
}
